import { Box, Snackbar, Alert } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";

interface EdineburghDeptScaleData {
  EDS_ID: string;
  CTIME: Date | null;
  PROCESS_TIME: Date | null;
  CUSER: string;
  LAUGH_FUNNY_THINGS: string;
  LOOK_FUTURE_HOPEFULLY: string;
  BAD_THINGS_BLAME_HERSELF: string;
  FEEL_DISTRESSED_ANXIOUS: string;
  FEEL_FEAR_PANIC: string;
  EVERYTHING_GETTING_HARDER: string;
  FEEL_SAD_HARD_SLEEP: string;
  FEEL_SAD_DEPRESSED: string;
  FEEL_SAD_CRY: string;
  THINK_HURT_MYSELF: string;
  FEEL_SAD_WANT_CRY: string;
  EDINBURGH_SCORE: number;
  NOTE: string;
  DATE_BIRTH: Date | null;
  NURSE_ID: string;
}

interface EdineburghDeptScaleProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof EdineburghDeptScaleData, value: any) => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function EdineburghDeptScaleCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  isSaving,
  extraAccess,
}: EdineburghDeptScaleProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "EDINEBURGH_DEPT_SCALE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof EdineburghDeptScaleData, value);
      });
    },
  });

  const typedFormData = formData as EdineburghDeptScaleData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof EdineburghDeptScaleData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = () => {
    if (!typedFormData) return;

    const scoreFields = [
      "LAUGH_FUNNY_THINGS_XVALUE",
      "LOOK_FUTURE_HOPEFULLY_XVALUE",
      "BAD_THINGS_BLAME_HERSELF_XVALUE",
      "FEEL_DISTRESSED_ANXIOUS_XVALUE",
      "FEEL_FEAR_PANIC_XVALUE",
      "EVERYTHING_GETTING_HARDER_XVALUE",
      "FEEL_SAD_HARD_SLEEP_XVALUE",
      "FEEL_SAD_DEPRESSED_XVALUE",
      "FEEL_SAD_CRY_XVALUE",
      "THINK_HURT_MYSELF_XVALUE",
      "FEEL_SAD_WANT_CRY_XVALUE",
    ];

    const score = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScore(score);
    handleChange("EDINBURGH_SCORE", score);
  };

  useEffect(() => {
    handleScoreCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typedFormData.LAUGH_FUNNY_THINGS,
    typedFormData.LOOK_FUTURE_HOPEFULLY,
    typedFormData.BAD_THINGS_BLAME_HERSELF,
    typedFormData.FEEL_DISTRESSED_ANXIOUS,
    typedFormData.FEEL_FEAR_PANIC,
    typedFormData.EVERYTHING_GETTING_HARDER,
    typedFormData.FEEL_SAD_HARD_SLEEP,
    typedFormData.FEEL_SAD_DEPRESSED,
    typedFormData.FEEL_SAD_CRY,
    typedFormData.THINK_HURT_MYSELF,
    typedFormData.FEEL_SAD_WANT_CRY,
  ]);


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.EDS_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={9}
            md={9}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("edineburgh-dept-scale.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.birth-date")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.DATE_BIRTH}
                  onChange={(date) => handleChange("DATE_BIRTH", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("edineburgh-dept-scale.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.LAUGH_FUNNY_THINGS")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.LAUGH_FUNNY_THINGS")}
                  mcpId={cardData.mcpConfig.LAUGH_FUNNY_THINGS.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("LAUGH_FUNNY_THINGS", selected);
                    }
                  }}
                  value={selectedMCPs.LAUGH_FUNNY_THINGS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.LAUGH_FUNNY_THINGS.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.LOOK_FUTURE_HOPEFULLY")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.LOOK_FUTURE_HOPEFULLY")}
                  mcpId={cardData.mcpConfig.LOOK_FUTURE_HOPEFULLY.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("LOOK_FUTURE_HOPEFULLY", selected);
                    }
                  }}
                  value={selectedMCPs.LOOK_FUTURE_HOPEFULLY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.LOOK_FUTURE_HOPEFULLY.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.BAD_THINGS_BLAME_HERSELF")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.BAD_THINGS_BLAME_HERSELF")}
                  mcpId={cardData.mcpConfig.BAD_THINGS_BLAME_HERSELF.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("BAD_THINGS_BLAME_HERSELF", selected);
                    }
                  }}
                  value={selectedMCPs.BAD_THINGS_BLAME_HERSELF}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.BAD_THINGS_BLAME_HERSELF.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.FEEL_DISTRESSED_ANXIOUS")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.FEEL_DISTRESSED_ANXIOUS")}
                  mcpId={cardData.mcpConfig.FEEL_DISTRESSED_ANXIOUS.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("FEEL_DISTRESSED_ANXIOUS", selected);
                    }
                  }}
                  value={selectedMCPs.FEEL_DISTRESSED_ANXIOUS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.FEEL_DISTRESSED_ANXIOUS.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.FEEL_FEAR_PANIC")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.FEEL_FEAR_PANIC")}
                  mcpId={cardData.mcpConfig.FEEL_FEAR_PANIC.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("FEEL_FEAR_PANIC", selected);
                    }
                  }}
                  value={selectedMCPs.FEEL_FEAR_PANIC}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.FEEL_FEAR_PANIC.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.EVERYTHING_GETTING_HARDER")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.EVERYTHING_GETTING_HARDER")}
                  mcpId={cardData.mcpConfig.EVERYTHING_GETTING_HARDER.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("EVERYTHING_GETTING_HARDER", selected);
                    }
                  }}
                  value={selectedMCPs.EVERYTHING_GETTING_HARDER}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.EVERYTHING_GETTING_HARDER.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.FEEL_SAD_HARD_SLEEP")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.FEEL_SAD_HARD_SLEEP")}
                  mcpId={cardData.mcpConfig.FEEL_SAD_HARD_SLEEP.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("FEEL_SAD_HARD_SLEEP", selected);
                    }
                  }}
                  value={selectedMCPs.FEEL_SAD_HARD_SLEEP}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.FEEL_SAD_HARD_SLEEP.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.FEEL_SAD_CRY")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.FEEL_SAD_CRY")}
                  mcpId={cardData.mcpConfig.FEEL_SAD_CRY.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("FEEL_SAD_CRY", selected);
                    }
                  }}
                  value={selectedMCPs.FEEL_SAD_CRY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.FEEL_SAD_CRY.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.FEEL_SAD_DEPRESSED")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.FEEL_SAD_DEPRESSED")}
                  mcpId={cardData.mcpConfig.FEEL_SAD_DEPRESSED.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("FEEL_SAD_DEPRESSED", selected);
                    }
                  }}
                  value={selectedMCPs.FEEL_SAD_DEPRESSED}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.FEEL_SAD_DEPRESSED.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.THINK_HURT_MYSELF")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.THINK_HURT_MYSELF")}
                  mcpId={cardData.mcpConfig.THINK_HURT_MYSELF.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("THINK_HURT_MYSELF", selected);
                    }
                  }}
                  value={selectedMCPs.THINK_HURT_MYSELF}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.THINK_HURT_MYSELF.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.FEEL_SAD_WANT_CRY")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("edineburgh-dept-scale.FEEL_SAD_WANT_CRY")}
                  mcpId={cardData.mcpConfig.FEEL_SAD_WANT_CRY.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("FEEL_SAD_WANT_CRY", selected);
                    }
                  }}
                  value={selectedMCPs.FEEL_SAD_WANT_CRY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.FEEL_SAD_WANT_CRY.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("edineburgh-dept-scale.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="left" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("edineburgh-dept-scale.note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.NOTE}
                  onChange={(e) => handleChange("NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default EdineburghDeptScaleCreate;
